// URS-F409: pagina Estadisticas.
// Filtros multiselect+search (Cliente / Estudio / Tipo / Subtipo) + rango
// de fechas obligatorio + toggle de visitas intermedias. Muestra una
// tabla por estudio con Coste real de voluntarios y Precio por Visita
// Ejecutada (PVP / visitas terminadas del estudio en el rango). El
// desglose por codigo de visita se muestra solo dentro de cada estudio
// al expandir su fila.
import { CaretDown, CaretUp, MagnifyingGlass, ChartBar, FileXls } from '@phosphor-icons/react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useLanguage } from '../contexts/LanguageContext';
import { formatEUR } from '../utils/formatCurrency';
import { visitTimeKey } from '../utils/visitClassification';
import AgendaEfficiencyPanel from './AgendaEfficiencyPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos hardcoded (decision 1a). Los mostramos con label amigable pero
// enviamos el valor lowercase tal como esta en la BD.
const TYPE_OPTIONS = [
  { value: 'cosmetico', label: 'Cosmético' },
  { value: 'sanitario', label: 'Producto Sanitario' },
  { value: 'suplemento', label: 'Suplemento Alimenticio' },
  { value: 'medicamento', label: 'Medicamento' },
];

// URS-F433: opciones del filtro de Estado del estudio.
const STATUS_OPTIONS_ES = [
  { value: 'activo', label: 'Activo' },
  { value: 'finalizado', label: 'Finalizado' },
  { value: 'cancelado', label: 'Cancelado' },
];
const STATUS_OPTIONS_EN = [
  { value: 'activo', label: 'Active' },
  { value: 'finalizado', label: 'Completed' },
  { value: 'cancelado', label: 'Cancelled' },
];

// URS-F409: componente reutilizable de filtro multiselect + buscador.
function MultiFilter({ testId, label, options, selected, onChange, lang }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o => (o.label || '').toLowerCase().includes(needle));
  }, [q, options]);
  const total = options.length;
  const selectedSet = new Set(selected);
  const allSelected = selected.length === 0;   // "" empty = all
  const displayLabel = allSelected
    ? (lang === 'es' ? `Todos (${total})` : `All (${total})`)
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label || selected[0])
      : (lang === 'es' ? `${selected.length}/${total} seleccionados` : `${selected.length}/${total} selected`);
  const toggle = (v) => {
    if (selectedSet.has(v)) onChange(selected.filter(x => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-stone-600">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            data-testid={`${testId}-trigger`}
            variant="outline"
            size="sm"
            className="w-full h-9 justify-between text-xs font-normal border-stone-200 bg-white"
          >
            <span className="truncate">{displayLabel}</span>
            <CaretDown size={12} className="shrink-0 text-stone-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-2 border-b border-stone-100">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-2.5 text-stone-400" />
              <Input
                data-testid={`${testId}-search`}
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={lang === 'es' ? 'Buscar...' : 'Search...'}
                className="h-8 text-xs pl-7 border-stone-200"
              />
            </div>
            <div className="flex items-center justify-between mt-2 px-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[11px] text-[#1f3e80] hover:underline"
              >
                {lang === 'es' ? 'Todos' : 'All'}
              </button>
              <span className="text-[10px] text-stone-400">{selected.length}/{total}</span>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-stone-400 italic px-3 py-2">
                {lang === 'es' ? 'Sin coincidencias' : 'No matches'}
              </p>
            ) : filtered.map(opt => (
              <label
                key={opt.value}
                data-testid={`${testId}-opt-${opt.value}`}
                className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-stone-50 cursor-pointer"
              >
                <Checkbox checked={selectedSet.has(opt.value)} onCheckedChange={() => toggle(opt.value)} />
                <span className="text-stone-700 flex-1 truncate">{opt.label}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function Estadisticas() {
  const { lang } = useLanguage();
  const lb = lang === 'es' ? {
    title: 'Estadísticas',
    subtitle: 'Coste real de voluntarios y precio por visita ejecutada',
    client: 'Cliente', study: 'Estudio', type: 'Tipo', subtype: 'Subtipo', status: 'Estado',
    date_from: 'Desde', date_to: 'Hasta',
    include_intermediate: 'Incluir visitas intermedias',
    apply: 'Aplicar filtros',
    export_xlsx: 'Exportar Excel',
    exporting: 'Exportando...',
    kpi_avg_price: 'Precio Medio Visita Ejecutada',
    kpi_avg_cost: 'Coste Medio Voluntario',
    kpi_avg_price_hint: 'PVP total / visitas ejecutadas',
    kpi_avg_cost_hint: 'Suma de pagos / voluntarios incluidos',
    kpi_avg_price_teo: 'Precio Medio Visita Teórico',
    kpi_avg_price_teo_hint: 'PVP total / visitas totales',
    kpi_visits_real: 'Diferencia de visitas',
    kpi_visits_real_hint: 'Visitas totales − ejecutadas',
    kpi_visits_total_label: 'Totales',
    kpi_visits_exec_label: 'Ejecutadas',
    per_study: 'Desglose por estudio',
    col_study: 'Estudio', col_client: 'Cliente', col_type: 'Tipo', col_status: 'Estado',
    col_visits: 'Visitas ejec.', col_visits_total: 'Visitas totales', col_executed_to_date: 'Ejecutado a día de hoy', col_pvp: 'PVP', col_cost: 'Coste voluntarios',
    col_ppv: 'Precio/Visita Ejec.', col_expand: '',
    no_results: 'No hay estudios con visitas terminadas en el rango seleccionado.',
    dates_required: 'Introduce el rango de fechas para aplicar los filtros.',
  } : {
    title: 'Statistics',
    subtitle: 'Real volunteer cost and executed price per visit',
    client: 'Client', study: 'Study', type: 'Type', subtype: 'Subtype', status: 'Status',
    date_from: 'From', date_to: 'To',
    include_intermediate: 'Include intermediate visits',
    apply: 'Apply filters',
    export_xlsx: 'Export Excel',
    exporting: 'Exporting...',
    kpi_avg_price: 'Executed Avg Price/Visit',
    kpi_avg_cost: 'Average Cost/Volunteer',
    kpi_avg_price_hint: 'Total PVP / executed visits',
    kpi_avg_cost_hint: 'Sum of payments / included volunteers',
    kpi_avg_price_teo: 'Theoretical Avg Price/Visit',
    kpi_avg_price_teo_hint: 'Total PVP / total visits',
    kpi_visits_real: 'Visit Difference',
    kpi_visits_real_hint: 'Total − executed visits',
    kpi_visits_total_label: 'Total',
    kpi_visits_exec_label: 'Executed',
    per_study: 'Per-study breakdown',
    col_study: 'Study', col_client: 'Client', col_type: 'Type', col_status: 'Status',
    col_visits: 'Exec. visits', col_visits_total: 'Total visits', col_executed_to_date: 'Executed to date', col_pvp: 'PVP', col_cost: 'Volunteer cost',
    col_ppv: 'Price/Exec. Visit', col_expand: '',
    no_results: 'No studies have terminated visits in the selected range.',
    dates_required: 'Please set a date range to apply filters.',
  };

  // Filter option catalogs
  const [clients, setClients] = useState([]);
  const [studies, setStudies] = useState([]);
  const [subtypeOptions, setSubtypeOptions] = useState([]);

  // Selected filters
  const [selClients, setSelClients] = useState([]);
  const [selStudies, setSelStudies] = useState([]);
  const [selTypes, setSelTypes] = useState([]);
  const [selSubtypes, setSelSubtypes] = useState([]);
  // URS-F433: filtro de Estado del estudio
  const [selStatuses, setSelStatuses] = useState([]);
  // Default rango: ultimos 12 meses (decision 3b)
  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); })();
  const [dateFrom, setDateFrom] = useState(yearAgo);
  const [dateTo, setDateTo] = useState(today);
  const [includeIntermediate, setIncludeIntermediate] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedStudy, setExpandedStudy] = useState(null);

  // Load catalogs
  useEffect(() => {
    axios.get(`${API_URL}/api/clients?limit=2000`, { withCredentials: true })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || data.data || []);
        setClients(items.map(c => ({ value: c.client_id, label: c.name || c.client_id })));
      }).catch(() => setClients([]));
    axios.get(`${API_URL}/api/studies?limit=2000`, { withCredentials: true })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || data.data || []);
        setStudies(items.map(s => ({ value: s.study_id, label: `${s.code || s.study_id} - ${s.title || ''}`.trim() })));
      }).catch(() => setStudies([]));
  }, []);

  // Load subtypes (cascade decision 2a) whenever types change
  useEffect(() => {
    const url = `${API_URL}/api/statistics/subtypes${selTypes.length ? `?types=${selTypes.join(',')}` : ''}`;
    axios.get(url, { withCredentials: true })
      .then(({ data }) => setSubtypeOptions((data.subtypes || []).map(s => ({ value: s, label: s }))))
      .catch(() => setSubtypeOptions([]));
    setSelSubtypes(prev => prev.filter(v => true));  // no reset here to keep multi-behavior consistent
  }, [selTypes]);

  const applyFilters = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/statistics/overview`, {
        client_ids: selClients,
        study_ids: selStudies,
        types: selTypes,
        subtypes: selSubtypes,
        statuses: selStatuses,
        date_from: dateFrom,
        date_to: dateTo,
        include_intermediate: includeIntermediate,
      }, { withCredentials: true });
      setData(data);
    } catch (err) {
      setData({ studies: [], totals: {}, visits_breakdown_global: {} });
    }
    setLoading(false);
  }, [selClients, selStudies, selTypes, selSubtypes, selStatuses, dateFrom, dateTo, includeIntermediate]);

  useEffect(() => { applyFilters(); }, [applyFilters]);

  const exportXlsx = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setExporting(true);
    try {
      const resp = await axios.post(`${API_URL}/api/statistics/overview/export.xlsx`, {
        client_ids: selClients,
        study_ids: selStudies,
        types: selTypes,
        subtypes: selSubtypes,
        statuses: selStatuses,
        date_from: dateFrom,
        date_to: dateTo,
        include_intermediate: includeIntermediate,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `estadisticas_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed', err);
    }
    setExporting(false);
  }, [selClients, selStudies, selTypes, selSubtypes, selStatuses, dateFrom, dateTo, includeIntermediate]);

  return (
    <div className="p-8 space-y-6" data-testid="estadisticas-page">
      <div className="flex items-center gap-3">
        <ChartBar size={26} className="text-[#1f3e80]" />
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-semibold text-stone-900">{lb.title}</h1>
          <p className="text-sm text-stone-500">{lb.subtitle}</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="overview" data-testid="stats-tab-overview">
            {lang === 'es' ? 'Coste y precio' : 'Cost & price'}
          </TabsTrigger>
          <TabsTrigger value="agenda-efficiency" data-testid="stats-tab-agenda-efficiency">
            {lang === 'es' ? 'Eficiencia de Agenda' : 'Agenda efficiency'}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="agenda-efficiency" className="mt-6">
          <AgendaEfficiencyPanel lang={lang} />
        </TabsContent>
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex-1" />
            <Button
              data-testid="stats-export-xlsx-btn"
              onClick={exportXlsx}
              disabled={exporting || loading || !dateFrom || !dateTo || !data || (data.studies || []).length === 0}
              variant="outline"
              className="h-9 gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            >
              <FileXls size={16} weight="regular" />
              {exporting ? lb.exporting : lb.export_xlsx}
            </Button>
          </div>

      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-sm p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <MultiFilter testId="stats-filter-client" label={lb.client} options={clients} selected={selClients} onChange={setSelClients} lang={lang} />
          <MultiFilter testId="stats-filter-study" label={lb.study} options={studies} selected={selStudies} onChange={setSelStudies} lang={lang} />
          <MultiFilter testId="stats-filter-type" label={lb.type} options={TYPE_OPTIONS} selected={selTypes} onChange={setSelTypes} lang={lang} />
          <MultiFilter testId="stats-filter-subtype" label={lb.subtype} options={subtypeOptions} selected={selSubtypes} onChange={setSelSubtypes} lang={lang} />
          <MultiFilter testId="stats-filter-status" label={lb.status} options={lang === 'es' ? STATUS_OPTIONS_ES : STATUS_OPTIONS_EN} selected={selStatuses} onChange={setSelStatuses} lang={lang} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-stone-600">{lb.date_from}</Label>
            <Input data-testid="stats-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs border-stone-200" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-stone-600">{lb.date_to}</Label>
            <Input data-testid="stats-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs border-stone-200" />
          </div>
          <label className="flex items-center gap-2 h-9 select-none cursor-pointer">
            <Switch
              data-testid="stats-toggle-intermediate"
              checked={includeIntermediate}
              onCheckedChange={setIncludeIntermediate}
            />
            <span className="text-xs text-stone-600">{lb.include_intermediate}</span>
          </label>
          <Button data-testid="stats-apply-btn" onClick={applyFilters} disabled={loading || !dateFrom || !dateTo} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white h-9 rounded-sm">
            {loading ? '...' : lb.apply}
          </Button>
        </div>
      </div>

      {/* KPIs top */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* URS-F479: Visitas Reales = totales - ejecutadas. Subvalores:
            totales y ejecutadas debajo del numero grande. */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-5">
          <p className="text-xs text-blue-700 font-medium">{lb.kpi_visits_real}</p>
          <p className="text-4xl font-heading font-bold text-blue-900 mt-2" data-testid="stats-kpi-visits-real">
            {data?.totals?.expected_visits_total == null || data?.totals?.executed_visits_total == null
              ? '—'
              : Math.max(0, (data.totals.expected_visits_total || 0) - (data.totals.executed_visits_total || 0))}
          </p>
          <div className="mt-1 space-y-0.5">
            <p className="text-[11px] text-blue-700/80" data-testid="stats-kpi-visits-total">
              {lb.kpi_visits_total_label}: <span className="font-mono font-semibold">{data?.totals?.expected_visits_total ?? '—'}</span>
            </p>
            <p className="text-[11px] text-blue-700/80" data-testid="stats-kpi-visits-exec">
              {lb.kpi_visits_exec_label}: <span className="font-mono font-semibold">{data?.totals?.executed_visits_total ?? '—'}</span>
            </p>
          </div>
        </div>
        {/* URS-F479: Precio Medio Visita Teorico = PVP / visitas totales. */}
        <div className="bg-sky-50 border border-sky-200 rounded-sm p-5">
          <p className="text-xs text-sky-700 font-medium">{lb.kpi_avg_price_teo}</p>
          <p className="text-4xl font-heading font-bold text-sky-900 mt-2" data-testid="stats-kpi-avg-price-teo">
            {data?.totals?.average_price_per_visit_theoretical == null
              ? '—'
              : formatEUR(data.totals.average_price_per_visit_theoretical)}
          </p>
          <p className="text-[11px] text-sky-700/70 italic mt-1">{lb.kpi_avg_price_teo_hint}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5">
          <p className="text-xs text-emerald-700 font-medium">{lb.kpi_avg_price}</p>
          <p className="text-4xl font-heading font-bold text-emerald-900 mt-2" data-testid="stats-kpi-avg-price">
            {data?.totals?.average_price_per_visit === null || data?.totals?.average_price_per_visit === undefined
              ? '—'
              : formatEUR(data.totals.average_price_per_visit)}
          </p>
          <p className="text-[11px] text-emerald-700/70 italic mt-1">{lb.kpi_avg_price_hint}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-5">
          <p className="text-xs text-amber-700 font-medium">{lb.kpi_avg_cost}</p>
          <p className="text-4xl font-heading font-bold text-amber-900 mt-2" data-testid="stats-kpi-avg-cost">
            {data?.totals?.average_cost_per_volunteer === null || data?.totals?.average_cost_per_volunteer === undefined
              ? '—'
              : formatEUR(data.totals.average_cost_per_volunteer)}
          </p>
          <p className="text-[11px] text-amber-700/70 italic mt-1">{lb.kpi_avg_cost_hint}</p>
        </div>
      </div>

      {/* Per-study table */}
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <h3 className="text-sm font-semibold text-stone-800 p-4 pb-2">{lb.per_study}</h3>
        {(!data || data.studies.length === 0) ? (
          <p className="p-6 text-sm text-stone-500 italic text-center">{lb.no_results}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 text-left text-xs text-stone-600 border-b border-stone-200">
                <th className="w-8 px-3 py-2"></th>
                <th className="px-3 py-2">{lb.col_study}</th>
                <th className="px-3 py-2">{lb.col_client}</th>
                <th className="px-3 py-2">{lb.col_type}</th>
                <th className="px-3 py-2">{lb.col_status}</th>
                <th className="px-3 py-2 text-right">{lb.col_visits}</th>
                <th className="px-3 py-2 text-right">{lb.col_visits_total}</th>
                <th className="px-3 py-2 text-right">{lb.col_executed_to_date}</th>
                <th className="px-3 py-2 text-right">{lb.col_pvp}</th>
                <th className="px-3 py-2 text-right">{lb.col_cost}</th>
                <th className="px-3 py-2 text-right">{lb.col_ppv}</th>
              </tr>
            </thead>
            <tbody>
              {data.studies.map(r => {
                const isOpen = expandedStudy === r.study_id;
                return [
                  <tr key={r.study_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`stats-row-${r.study_id}`}>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        data-testid={`stats-expand-${r.study_id}`}
                        onClick={() => setExpandedStudy(isOpen ? null : r.study_id)}
                        className="text-stone-400 hover:text-[#1f3e80]"
                      >
                        {isOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-stone-800">{r.study_code}</div>
                      <div className="text-[11px] text-stone-500 truncate max-w-[280px]">{r.study_title}</div>
                    </td>
                    <td className="px-3 py-2 text-stone-700">{r.client_name || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{r.product_type || '—'}{r.phase ? ` · ${r.phase}` : ''}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {(() => {
                        // URS-F433: badge de Estado del estudio.
                        const st = (r.status || '').toLowerCase();
                        const label = st === 'activo' ? (lang === 'es' ? 'Activo' : 'Active')
                          : st === 'finalizado' ? (lang === 'es' ? 'Finalizado' : 'Completed')
                          : st === 'cancelado' ? (lang === 'es' ? 'Cancelado' : 'Cancelled')
                          : (r.status || '—');
                        const cls = st === 'activo' ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                          : st === 'finalizado' ? 'bg-stone-100 border-stone-300 text-stone-700'
                          : st === 'cancelado' ? 'bg-red-100 border-red-300 text-red-800'
                          : 'bg-stone-50 border-stone-200 text-stone-500';
                        return (
                          <span
                            data-testid={`stats-status-${r.study_id}`}
                            className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-medium ${cls}`}
                          >{label}</span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-[#1f3e80]" data-testid={`stats-executed-${r.study_id}`}>{r.executed_visits_total}</td>
                    <td className="px-3 py-2 text-right font-mono text-stone-700" data-testid={`stats-expected-${r.study_id}`}>{r.expected_visits_total || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-900" data-testid={`stats-executed-to-date-${r.study_id}`}>
                      {/* URS-F432: se copia el valor calculado en el backend con la misma
                          formula que la pestaña Costes del estudio (no se calcula aqui). */}
                      {r.executed_to_date === null || r.executed_to_date === undefined ? '—' : formatEUR(r.executed_to_date)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-stone-600">{r.pvp > 0 ? formatEUR(r.pvp) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-900">{formatEUR(r.cost_total_volunteers)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-800">{r.price_per_executed_visit === null ? '—' : formatEUR(r.price_per_executed_visit)}</td>
                  </tr>,
                  isOpen && (
                    <tr key={`${r.study_id}-detail`} className="bg-stone-50/60 border-b border-stone-100">
                      <td colSpan={11} className="px-6 py-3">
                        <p className="text-[11px] font-semibold text-stone-600 mb-2">
                          {lang === 'es' ? 'Visitas ejecutadas por código' : 'Executed visits by code'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(r.executed_visits_by_code)
                            .sort((a, b) => visitTimeKey(a[0]) - visitTimeKey(b[0]))
                            .map(([code, count]) => (
                              <Badge key={code} variant="outline" className="text-xs font-mono bg-white" data-testid={`stats-detail-${r.study_id}-${code}`}>
                                {code}: <span className="ml-1 font-semibold">{count}</span>
                              </Badge>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        )}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
